import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrophy, FiRotateCcw, FiHome, FiSave, FiUser } = FiIcons;

function GameOverModal({ isOpen, score, level, streak, rounds, onRestart, onMenu, settings }) {
  const [playerName, setPlayerName] = useState('');
  const [scoreSaved, setScoreSaved] = useState(false);

  const saveScore = () => {
    if (!playerName.trim()) return;

    const scoreData = {
      playerName: playerName.trim(),
      score,
      level,
      streak,
      rounds,
      difficulty: settings.difficulty,
      gameMode: settings.gameMode,
      timestamp: Date.now(),
      date: new Date().toISOString()
    };

    // Get existing scores
    const existingScores = JSON.parse(localStorage.getItem('colorMixScores') || '[]');
    
    // Add new score
    existingScores.push(scoreData);
    
    // Sort by score (descending) and keep top 100
    existingScores.sort((a, b) => b.score - a.score);
    const topScores = existingScores.slice(0, 100);
    
    // Save back to localStorage
    localStorage.setItem('colorMixScores', JSON.stringify(topScores));
    localStorage.setItem('lastSavedScore', JSON.stringify(scoreData));
    
    setScoreSaved(true);
  };

  const getRank = () => {
    if (score >= 10000) return { emoji: '🏆', title: 'Color Master' };
    if (score >= 5000) return { emoji: '🥇', title: 'Expert Mixer' };
    if (score >= 2000) return { emoji: '🥈', title: 'Color Pro' };
    if (score >= 1000) return { emoji: '🥉', title: 'Rising Star' };
    return { emoji: '🎨', title: 'Color Apprentice' };
  };

  const rank = getRank();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 50 }}
          className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-3xl p-6 w-full max-w-md border border-white/20"
        >
          <div className="text-center mb-6">
            <div className="text-6xl mb-2">{rank.emoji}</div>
            <h2 className="text-3xl font-bold text-white mb-2">Game Over!</h2>
            <p className="text-gray-300">{rank.title}</p>
          </div>

          <div className="bg-white/10 rounded-2xl p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-gray-300 text-sm">Final Score</p>
                <p className="text-white font-bold text-2xl">{score.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-300 text-sm">Level Reached</p>
                <p className="text-white font-bold text-2xl">{level}</p>
              </div>
              <div>
                <p className="text-gray-300 text-sm">Best Streak</p>
                <p className="text-white font-bold text-2xl">{streak}</p>
              </div>
              <div>
                <p className="text-gray-300 text-sm">Rounds Played</p>
                <p className="text-white font-bold text-2xl">{rounds}</p>
              </div>
            </div>
          </div>

          {!scoreSaved && (
            <div className="mb-6">
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  placeholder="Enter your name"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  maxLength={20}
                  className="flex-1 bg-white/20 text-white placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={saveScore}
                  disabled={!playerName.trim()}
                  className="bg-green-500 text-white px-4 py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SafeIcon icon={FiSave} />
                </motion.button>
              </div>
              <p className="text-gray-400 text-xs text-center">
                Save your score to appear on the leaderboard
              </p>
            </div>
          )}

          {scoreSaved && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 mb-6 text-center"
            >
              <SafeIcon icon={FiTrophy} className="text-green-400 text-2xl mx-auto mb-2" />
              <p className="text-green-400 font-medium">Score saved to leaderboard!</p>
            </motion.div>
          )}

          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onRestart}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiRotateCcw} />
              Play Again
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onMenu}
              className="flex-1 bg-white/20 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiHome} />
              Menu
            </motion.button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default GameOverModal;